import { DocumentSmartTextDateFormat, DocumentSmartTextType } from "./types";

const ISO_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})/;
const ISO_DATETIME_TIME_RE = /T(\d{2}):(\d{2})/;

const applyDatePattern = (
  yyyy: string,
  mm: string,
  dd: string,
  pattern: DocumentSmartTextDateFormat
): string => {
  if (pattern === "MM/DD/YYYY") return `${mm}/${dd}/${yyyy}`;
  return `${dd}/${mm}/${yyyy}`;
};

/**
 * Formats an ISO date / datetime-local value using the supplied pattern.
 *
 * - `date` type values look like `YYYY-MM-DD` (from `<input type="date">`).
 * - `datetime` type values look like `YYYY-MM-DDTHH:mm` (from
 *   `<input type="datetime-local">`); the date portion is reformatted and the
 *   `HH:mm` time portion is appended unchanged.
 *
 * Returns the raw input unchanged when:
 *   - the value is empty / not a string,
 *   - the type isn't `date` / `datetime`,
 *   - the value doesn't match the expected ISO shape (defensive fallback so
 *     half-typed or unexpected values never break textbox rendering).
 *
 * When `pattern` is undefined for a date/datetime, defaults to `DD/MM/YYYY` —
 * keeps legacy fields (stored before `format` was persisted) rendering
 * consistently with newly-created ones, which now write the default on
 * creation via `addDocumentSmartTextField`.
 */
export const formatDocumentDateValue = (
  rawValue: string | null | undefined,
  type: DocumentSmartTextType,
  pattern: DocumentSmartTextDateFormat | undefined
): string => {
  if (!rawValue) return rawValue ?? "";
  if (type !== "date" && type !== "datetime") return rawValue;
  const effectivePattern: DocumentSmartTextDateFormat = pattern ?? "DD/MM/YYYY";

  const dateMatch = rawValue.match(ISO_DATE_RE);
  if (!dateMatch) return rawValue;
  const yyyy = dateMatch[1];
  const mm = dateMatch[2];
  const dd = dateMatch[3];
  if (!yyyy || !mm || !dd) return rawValue;
  const formattedDate = applyDatePattern(yyyy, mm, dd, effectivePattern);

  if (type !== "datetime") return formattedDate;

  const timeMatch = rawValue.match(ISO_DATETIME_TIME_RE);
  if (!timeMatch) return formattedDate;
  const hh = timeMatch[1];
  const min = timeMatch[2];
  if (!hh || !min) return formattedDate;
  return `${formattedDate} ${hh}:${min}`;
};
